import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const AnalyticsPage = () => {
  const navigate = useNavigate()
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState('questions') // 'questions', 'users', 'demographics'
  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState('responses') // for questions: 'responses', 'text', 'theme' | for users: 'score', 'identity', 'souls'

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo({ top: 0, behavior: 'smooth' })
    fetchAnalytics()
  }, [])

  const fetchAnalytics = async () => {
    try {
      setLoading(true)
      // In production, use window.location.origin. In local dev, use explicit backend URL
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== 'common-ground.local'
      const serverUrl = isProduction ? window.location.origin : (import.meta.env.VITE_SERVER_URL || 'http://common-ground.local:8000')
      
      // Fetch both analytics and demographics data
      const [analyticsResponse, demographicsResponse] = await Promise.all([
        fetch(`${serverUrl}${isProduction ? '/api/analytics/all' : '/analytics/all'}`),
        fetch(`${serverUrl}${isProduction ? '/api/analytics/demographics' : '/analytics/demographics'}`)
      ])
      
      if (analyticsResponse.ok && demographicsResponse.ok) {
        const [analyticsData, demographicsData] = await Promise.all([
          analyticsResponse.json(),
          demographicsResponse.json()
        ])
        setAnalytics({
          ...analyticsData,
          demographics: demographicsData
        })
      } else {
        setError('Failed to fetch analytics')
      }
    } catch (error) {
      setError(`Error fetching analytics: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const filteredQuestions = analytics?.questions.data.filter(q => 
    q.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.theme.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  const filteredUsers = analytics?.users.data.filter(u => 
    u.identity.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    switch (sortBy) {
      case 'responses':
        return b.total_responses - a.total_responses
      case 'text':
        return a.text.localeCompare(b.text)
      case 'theme':
        return a.theme.localeCompare(b.theme)
      default:
        return 0
    }
  })

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    // Sort by souls saved first (descending), then by common ground percentage (descending)
    const soulsDiff = (b.souls_helped || 0) - (a.souls_helped || 0)
    if (soulsDiff !== 0) {
      return soulsDiff
    }
    // If souls saved are equal, sort by common ground percentage
    return b.common_ground_percentage - a.common_ground_percentage
  })

  const getTopAnswer = (question) => {
    if (!question.answer_distribution.length) return null
    const topAnswer = question.answer_distribution.reduce((prev, current) => 
      current.count > prev.count ? current : prev
    )
    // Only return if there are actual responses
    return topAnswer.count > 0 ? topAnswer : null
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-purgatory-dark">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-4 border-purgatory-ash border-t-neon-green rounded-full mx-auto mb-4"></div>
          <p className="text-purgatory-mist">Loading analytics...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-purgatory-dark">
        <div className="text-center">
          <p className="text-purgatory-ember mb-4">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="btn-retro"
          >
            Return Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-purgatory-dark text-purgatory-light p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-6xl font-bold text-neon-green mb-4">
            📊 Question Analytics
          </h1>
          <p className="text-purgatory-mist mb-4">
            Analyzing {analytics?.questions.total_questions || 0} questions and {analytics?.users.total_users || 0} users
          </p>
          <button 
            onClick={() => navigate('/')}
            className="btn-retro"
          >
            ← Back to Home
          </button>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-6">
          <div className="flex bg-purgatory-ash rounded-lg p-1">
            <button
              onClick={() => {setActiveTab('questions'); setSortBy('responses'); setSearchTerm('')}}
              className={`px-6 py-2 rounded-md transition-all ${
                activeTab === 'questions' 
                  ? 'bg-neon-green text-purgatory-dark font-semibold' 
                  : 'text-purgatory-mist hover:text-purgatory-light'
              }`}
            >
              📝 Questions
            </button>
            <button
              onClick={() => {setActiveTab('users'); setSearchTerm('')}}
              className={`px-6 py-2 rounded-md transition-all ${
                activeTab === 'users' 
                  ? 'bg-neon-green text-purgatory-dark font-semibold' 
                  : 'text-purgatory-mist hover:text-purgatory-light'
              }`}
            >
              👥 Users
            </button>
            <button
              onClick={() => {setActiveTab('demographics'); setSearchTerm('')}}
              className={`px-6 py-2 rounded-md transition-all ${
                activeTab === 'demographics' 
                  ? 'bg-neon-green text-purgatory-dark font-semibold' 
                  : 'text-purgatory-mist hover:text-purgatory-light'
              }`}
            >
              🌍 Demographics
            </button>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder={
                activeTab === 'questions' ? "Search questions or themes..." : 
                activeTab === 'users' ? "Search users..." : 
                "Search languages or timezones..."
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-purgatory-ash border border-purgatory-mist rounded text-purgatory-light placeholder-purgatory-mist focus:border-neon-green focus:outline-none"
            />
          </div>
          {activeTab === 'questions' && (
            <div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 bg-purgatory-ash border border-purgatory-mist rounded text-purgatory-light focus:border-neon-green focus:outline-none"
              >
                <option value="responses">Sort by Responses</option>
                <option value="text">Sort by Question</option>
                <option value="theme">Sort by Theme</option>
              </select>
            </div>
          )}
        </div>

        {/* Summary Stats */}
        {activeTab === 'questions' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-neon-green mb-1">
                {sortedQuestions.length}
              </div>
              <div className="text-sm text-purgatory-mist">Questions Shown</div>
            </div>
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-purgatory-gold mb-1">
                {sortedQuestions.reduce((sum, q) => sum + q.total_responses, 0)}
              </div>
              <div className="text-sm text-purgatory-mist">Total Responses</div>
            </div>
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-neon-blue mb-1">
                {Math.round(sortedQuestions.reduce((sum, q) => sum + q.total_responses, 0) / Math.max(1, sortedQuestions.length))}
              </div>
              <div className="text-sm text-purgatory-mist">Avg Responses/Question</div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-neon-green mb-1">
                {sortedUsers.length}
              </div>
              <div className="text-sm text-purgatory-mist">Users Shown</div>
            </div>
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-purgatory-gold mb-1">
                {sortedUsers.reduce((sum, u) => sum + u.total_questions, 0)}
              </div>
              <div className="text-sm text-purgatory-mist">Total Questions</div>
            </div>
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-neon-blue mb-1">
                {sortedUsers.reduce((sum, u) => sum + (u.souls_helped || 0), 0)}
              </div>
              <div className="text-sm text-purgatory-mist">Souls Saved</div>
            </div>
            <div className="card-retro p-4 text-center">
              <div className="text-2xl font-bold text-purgatory-ember mb-1">
                {sortedUsers.length > 0 ? Math.round(sortedUsers.reduce((sum, u) => sum + u.common_ground_percentage, 0) / sortedUsers.length) : 0}%
              </div>
              <div className="text-sm text-purgatory-mist">Avg Common Ground</div>
            </div>
          </div>
        )}

        {/* Content - Questions, Users, or Demographics */}
        <div className="space-y-6">
          {activeTab === 'questions' ? (
            // Questions List
            sortedQuestions.map((question) => {
            const topAnswer = getTopAnswer(question)
            return (
              <div key={question.id} className="card-retro p-6">
                {/* Question Header */}
                <div className="mb-4">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-1 bg-purgatory-ash rounded text-purgatory-mist">
                      #{question.id}
                    </span>
                    <span className="text-xs px-2 py-1 bg-neon-blue bg-opacity-20 border border-neon-blue rounded text-neon-blue">
                      {question.theme}
                    </span>
                    <span className="text-xs px-2 py-1 bg-purgatory-gold bg-opacity-20 border border-purgatory-gold rounded text-purgatory-gold">
                      {question.total_responses} responses
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-purgatory-light mb-2">
                    {question.text}
                  </h3>
                  {topAnswer && question.total_responses > 0 && (
                    <p className="text-sm text-neon-green">
                      Most popular: "{topAnswer.option}" ({topAnswer.percentage}%)
                    </p>
                  )}
                </div>

                {/* Answer Distribution */}
                <div className="space-y-3">
                  {question.answer_distribution.map((answer, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-4 text-center text-xs text-purgatory-mist">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-purgatory-light">
                            {answer.option}
                          </span>
                          <span className="text-xs text-purgatory-mist">
                            {answer.count} ({answer.percentage}%)
                          </span>
                        </div>
                        <div className="w-full bg-purgatory-ash rounded-full h-2">
                          <div 
                            className="h-2 rounded-full transition-all duration-300"
                            style={{
                              width: `${answer.percentage}%`,
                              backgroundColor: answer.count > 0 ? (
                                answer.percentage > 50 ? '#00ff00' :
                                answer.percentage > 25 ? '#f7c52d' : '#00bfff'
                              ) : '#4a5568'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {question.total_responses === 0 && (
                  <div className="text-center py-4 text-purgatory-mist text-sm">
                    No responses yet
                  </div>
                )}
              </div>
            )
          })
          ) : activeTab === 'users' ? (
            sortedUsers.map((user) => (
              <div key={user.id} className="card-retro p-6">
                {/* User Header */}
                <div className="text-center mb-4">
                  <div className="text-4xl mb-3">{user.identity}</div>
                  <div className="flex flex-wrap justify-center gap-2">
                    <span className="text-xs px-2 py-1 bg-neon-green bg-opacity-20 border border-neon-green rounded text-neon-green">
                      {user.common_ground_percentage}% Common Ground
                    </span>
                    <span className="text-xs px-2 py-1 bg-purgatory-gold bg-opacity-20 border border-purgatory-gold rounded text-purgatory-gold">
                      {user.souls_helped || 0} Souls Saved
                    </span>
                    <span className="text-xs px-2 py-1 bg-purgatory-ember bg-opacity-20 border border-purgatory-ember rounded text-purgatory-ember">
                      {user.failed_escapes || 0} Failed Escapes
                    </span>
                    {user.recent_rounds > 0 && (
                      <span className="text-xs px-2 py-1 bg-neon-blue bg-opacity-20 border border-neon-blue rounded text-neon-blue">
                        {user.recent_rounds} recent rounds
                      </span>
                    )}
                  </div>
                </div>

                {/* User Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-neon-green mb-1">
                      {user.total_partnerships}
                    </div>
                    <div className="text-xs text-purgatory-mist">Partnerships</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purgatory-gold mb-1">
                      {user.total_rounds}
                    </div>
                    <div className="text-xs text-purgatory-mist">Rounds Played</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-neon-blue mb-1">
                      {user.total_questions}
                    </div>
                    <div className="text-xs text-purgatory-mist">Questions Asked</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purgatory-ember mb-1">
                      {user.total_matches}
                    </div>
                    <div className="text-xs text-purgatory-mist">Matched Answers</div>
                  </div>
                </div>

                {/* Common Ground Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-purgatory-light font-semibold">
                      Common Ground Score
                    </span>
                    <span className="text-sm text-purgatory-mist">
                      {user.total_matches}/{user.total_questions} ({user.common_ground_percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-purgatory-ash rounded-full h-3">
                    <div 
                      className="h-3 rounded-full transition-all duration-300"
                      style={{
                        width: `${user.common_ground_percentage}%`,
                        backgroundColor: 
                          user.common_ground_percentage >= 70 ? '#00ff00' :
                          user.common_ground_percentage >= 50 ? '#f7c52d' : 
                          user.common_ground_percentage >= 30 ? '#00bfff' : '#ff6b6b'
                      }}
                    />
                  </div>
                </div>

                {user.total_questions === 0 && (
                  <div className="text-center py-4 text-purgatory-mist text-sm">
                    No game activity yet
                  </div>
                )}
              </div>
            ))
          ) : activeTab === 'demographics' ? (
            // Demographics Content
            analytics?.demographics ? (
              <div className="space-y-8">
                {/* Overview Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="card-retro p-4 text-center">
                    <div className="text-2xl font-bold text-neon-green mb-1">
                      {analytics.demographics.total_users}
                    </div>
                    <div className="text-sm text-purgatory-mist">Total Users</div>
                  </div>
                  <div className="card-retro p-4 text-center">
                    <div className="text-2xl font-bold text-neon-blue mb-1">
                      {analytics.demographics.users_with_language}
                    </div>
                    <div className="text-sm text-purgatory-mist">With Language Data</div>
                  </div>
                  <div className="card-retro p-4 text-center">
                    <div className="text-2xl font-bold text-purgatory-ember mb-1">
                      {analytics.demographics.users_with_timezone}
                    </div>
                    <div className="text-sm text-purgatory-mist">With Timezone Data</div>
                  </div>
                  <div className="card-retro p-4 text-center">
                    <div className="text-2xl font-bold text-purgatory-gold mb-1">
                      {analytics.demographics.language_coverage}%
                    </div>
                    <div className="text-sm text-purgatory-mist">Language Coverage</div>
                  </div>
                </div>

                {/* Languages */}
                <div className="card-retro p-6">
                  <h3 className="text-xl font-bold text-purgatory-gold mb-4 flex items-center">
                    🌍 Languages ({analytics.demographics.languages.length})
                  </h3>
                  <div className="space-y-3">
                    {analytics.demographics.languages
                      .filter(lang => 
                        !searchTerm || 
                        lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        lang.primary.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((lang, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-purgatory-dark rounded border border-purgatory-ash">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">
                              {lang.code === 'en' ? '🇺🇸' : 
                               lang.code === 'es' ? '🇪🇸' : 
                               lang.code === 'fr' ? '🇫🇷' : 
                               lang.code === 'de' ? '🇩🇪' : 
                               lang.code === 'ja' ? '🇯🇵' : 
                               lang.code === 'zh' ? '🇨🇳' : '🌍'}
                            </div>
                            <div>
                              <div className="font-semibold text-purgatory-light">{lang.name}</div>
                              <div className="text-sm text-purgatory-mist">{lang.primary}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-neon-green">{lang.count}</div>
                            <div className="text-sm text-purgatory-mist">{lang.percentage}%</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Timezones */}
                <div className="card-retro p-6">
                  <h3 className="text-xl font-bold text-purgatory-gold mb-4 flex items-center">
                    🕐 Timezones ({analytics.demographics.timezones.length})
                  </h3>
                  <div className="space-y-3">
                    {analytics.demographics.timezones
                      .filter(tz => 
                        !searchTerm || 
                        tz.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        tz.id.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((tz, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-purgatory-dark rounded border border-purgatory-ash">
                          <div className="flex items-center space-x-3">
                            <div className="text-2xl">
                              {tz.offset.startsWith('+') ? '🌅' : '🌙'}
                            </div>
                            <div>
                              <div className="font-semibold text-purgatory-light">{tz.name}</div>
                              <div className="text-sm text-purgatory-mist">{tz.id} ({tz.offset})</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold text-neon-blue">{tz.count}</div>
                            <div className="text-sm text-purgatory-mist">{tz.percentage}%</div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-purgatory-mist">
                No demographics data available
              </div>
            )
          ) : null}
        </div>

        {/* No Results Messages */}
        {activeTab === 'questions' && sortedQuestions.length === 0 && searchTerm && (
          <div className="text-center py-8 text-purgatory-mist">
            No questions found matching "{searchTerm}"
          </div>
        )}
        
        {activeTab === 'users' && sortedUsers.length === 0 && searchTerm && (
          <div className="text-center py-8 text-purgatory-mist">
            No users found matching "{searchTerm}"
          </div>
        )}
        
        {activeTab === 'demographics' && analytics?.demographics && 
         analytics.demographics.languages.filter(lang => 
           !searchTerm || 
           lang.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           lang.primary.toLowerCase().includes(searchTerm.toLowerCase())
         ).length === 0 && 
         analytics.demographics.timezones.filter(tz => 
           !searchTerm || 
           tz.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
           tz.id.toLowerCase().includes(searchTerm.toLowerCase())
         ).length === 0 && searchTerm && (
          <div className="text-center py-8 text-purgatory-mist">
            No languages or timezones found matching "{searchTerm}"
          </div>
        )}
      </div>
    </div>
  )
}

export default AnalyticsPage
